import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Radio } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatRelative } from "@/lib/format";

const empty = { name: "", ip_address: "", site_id: "", radius_secret: "changeme", identity: "", mikrotik_user: "", mikrotik_password: "", api_port: 8728, active: true };
const PAGE_SIZE = 20;

export default function Routers() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (p) => {
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/admin/routers", { params: { page: p, page_size: PAGE_SIZE } }),
        api.get("/admin/sites", { params: { page_size: 1000 } }), // full list for the site picker
      ]);
      setItems(r.items); setTotal(r.total); setSites(s.items);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(page); }, [page]);

  const submit = async () => {
    try {
      if (!form.site_id) { toast.error("Please select a site"); return; }
      if (editing) await api.put(`/admin/routers/${editing}`, form);
      else await api.post("/admin/routers", form);
      toast.success("Saved");
      setOpen(false);
      await load(page);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this router?")) return;
    try {
      await api.delete(`/admin/routers/${id}`);
      toast.success("Deleted");
      await load(page);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const ping = async (id) => {
    try {
      await api.post(`/admin/routers/${id}/ping`);
      toast.success("Router online");
      await load(page);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <PageHeader
        testId="routers-header"
        title="MikroTik Routers"
        subtitle="RADIUS-connected hotspot devices across all sites"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-router-btn"
                onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                className="h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
              >
                <Plus className="h-4 w-4 mr-1" /> NEW ROUTER
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Edit Router" : "New Router"}</DialogTitle>
                <DialogDescription className="text-nhp-muted">MikroTik router details for RADIUS provisioning.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Name / Identity</Label>
                  <Input data-testid="router-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">IP Address</Label>
                  <Input data-testid="router-ip" value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Site</Label>
                  <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                    <SelectTrigger data-testid="router-site" className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10">
                      <SelectValue placeholder="Choose site" />
                    </SelectTrigger>
                    <SelectContent className="bg-nhp-surface border-nhp-border text-nhp-text">
                      {sites.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">RADIUS Secret</Label>
                  <Input type="password" value={form.radius_secret} onChange={(e) => setForm({ ...form, radius_secret: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">API Port</Label>
                    <Input type="number" value={form.api_port} onChange={(e) => setForm({ ...form, api_port: +e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">MT User</Label>
                    <Input value={form.mikrotik_user} onChange={(e) => setForm({ ...form, mikrotik_user: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" placeholder="admin" />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">MT Password</Label>
                    <Input type="password" value={form.mikrotik_password} onChange={(e) => setForm({ ...form, mikrotik_password: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-nhp-border px-3 py-2">
                  <Label>Active</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button data-testid="router-save-btn" onClick={submit} className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">SAVE</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 md:p-8">
        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">IP</div>
            <div className="col-span-2">Site</div>
            <div className="col-span-2">Last Seen</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {items.length === 0 && <div className="p-8 text-center text-sm text-nhp-muted">No routers yet.</div>}
            {items.map((r) => (
              <div key={r.id} data-testid={`router-row-${r.id}`} className="flex flex-col gap-2 md:grid md:grid-cols-12 px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                <div className="flex items-start justify-between gap-3 md:contents">
                  <div className="md:col-span-3">
                    <div className="font-medium flex items-center gap-2">
                      <Radio className={`h-3.5 w-3.5 shrink-0 ${r.status === "online" ? "text-nhp-primary" : "text-nhp-muted"}`} />
                      {r.name}
                    </div>
                    <div className="text-xs text-nhp-muted font-mono">{r.identity}</div>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border md:col-span-1 md:order-4 ${r.status === "online" ? "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10" : "text-nhp-critical border-nhp-critical/40"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nhp-muted md:contents">
                  <div className="font-mono md:col-span-2 text-nhp-text">{r.ip_address}</div>
                  <div className="md:col-span-2">{r.site_name || "-"}</div>
                  <div className="font-mono text-xs md:col-span-2">{r.last_seen ? formatRelative(r.last_seen) : "Never"}</div>
                </div>
                <div className="flex justify-end gap-1 md:col-span-2 md:order-5">
                  <Button size="sm" variant="ghost" onClick={() => ping(r.id)} className="h-8 rounded-none text-nhp-primary hover:text-nhp-text font-mono text-xs">PING</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(r.id); setForm({ name: r.name, ip_address: r.ip_address, site_id: r.site_id, radius_secret: r.radius_secret, identity: r.identity || "", mikrotik_user: r.mikrotik_user || "", mikrotik_password: r.mikrotik_password || "", api_port: r.api_port || 8728, active: r.active }); setOpen(true); }} className="h-8 rounded-none text-nhp-muted hover:text-nhp-text">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="h-8 rounded-none text-nhp-muted hover:text-nhp-critical">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="routers" onChange={setPage} testId="routers" />
        </div>
      </div>
    </div>
  );
}
